import {
  StatusBarColorsV4,
  StatusBarStatusColorsV4,
  StatusBarV4,
} from '@labrav/react-components'
import {
  MsftCombinedFormState,
  MsftCombinedFormStatus,
} from '../../../modules/microsoftCombinedForm/reducer'
import { useStyles } from './Sections.styles'
import { useSelector } from '../../../store'
import { Typography } from '@mui/material'
import { getExpirationFromDate } from '../../../common/utils/dates'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { useMemo } from 'react'
import { productDetailDate } from '../../../common/utils/productCreationUtil'
import { ViewError } from '../../ViewError/ViewError'

export const CombinedFormHeader = ({
  microsoftCombinedFormDetails,
}: {
  microsoftCombinedFormDetails: MsftCombinedFormState
}) => {
  let status: JSX.Element | null = null
  const offerStatus = microsoftCombinedFormDetails?.status
  const { classes } = useStyles()
  const statusLogs = microsoftCombinedFormDetails?.statusLogs
  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const { isExpriringSoon, dayDiff, isExpired } = getExpirationFromDate(
    microsoftCombinedFormDetails?.draftOffer?.acceptBy as string,
    5
  )
  const offerExpiringSoon =
    isExpriringSoon &&
    [
      MsftCombinedFormStatus.CHANNEL_OFFER_EXTENDED_TO_BUYER,
      MsftCombinedFormStatus.RESALE_AUTHORIZATION_CREATED,
      MsftCombinedFormStatus.CHANNEL_OFFER_CREATED,
    ].includes(offerStatus as MsftCombinedFormStatus)

  const notExpiringSoonAndNotExpired = !offerExpiringSoon && !isExpired

  const showOfferExpiredStatusBar =
    isExpired &&
    [
      MsftCombinedFormStatus.CHANNEL_OFFER_EXTENDED_TO_BUYER,
      MsftCombinedFormStatus.RESALE_AUTHORIZATION_CREATED,
      MsftCombinedFormStatus.CHANNEL_OFFER_CREATED,
    ].includes(offerStatus as MsftCombinedFormStatus)

  const {
    modifiedDate: channelOfferAcceptedDate,
    modifiedTime: channelOfferAcceptedTime,
  } =
    statusLogs?.channelOfferAccepted &&
    statusLogs?.channelOfferAccepted !== null
      ? productDetailDate(statusLogs?.channelOfferAccepted, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const {
    modifiedDate: offerExpirationDateDisplay,
    modifiedTime: offerExpirationDateTime,
  } = microsoftCombinedFormDetails?.draftOffer?.acceptBy
    ? productDetailDate(
        microsoftCombinedFormDetails?.draftOffer?.acceptBy,
        timeZone
      )
    : { modifiedDate: '', modifiedTime: '' }
  const fullDateExpirationTimeForHeader = `${offerExpirationDateDisplay} ${offerExpirationDateTime}`

  const { modifiedDate: offerRejectedDate, modifiedTime: offerRejectedTime } =
    statusLogs?.rejected && statusLogs?.rejected !== null
      ? productDetailDate(statusLogs?.rejected, timeZone)
      : { modifiedDate: '', modifiedTime: '' }
  const fullDateRejectedTimeForHeader = `${offerRejectedDate} | ${offerRejectedTime}`

  type CombinedOfferStatus =
    | 'failed'
    | 'rejected'
    | 'accepted'
    | 'in_progress'
    | 'extended_to_buyer'
    | 'expiring_soon'
    | 'expired'

  const combinedOfferStatus = useMemo<CombinedOfferStatus>(() => {
    if (
      offerStatus ===
        MsftCombinedFormStatus.RESALE_AUTHORIZATION_CREATION_IN_PROGRESS ||
      offerStatus === MsftCombinedFormStatus.CHANNEL_OFFER_CREATION_IN_PROGRESS
    )
      return 'in_progress'
    if (offerStatus === MsftCombinedFormStatus.CHANNEL_OFFER_ACCEPTED)
      return 'accepted'
    if (
      notExpiringSoonAndNotExpired &&
      offerStatus === MsftCombinedFormStatus.CHANNEL_OFFER_EXTENDED_TO_BUYER
    )
      return 'extended_to_buyer'
    if (offerStatus === MsftCombinedFormStatus.REJECTED) return 'rejected'
    if (
      offerStatus === MsftCombinedFormStatus.CHANNEL_OFFER_CREATION_FAILED ||
      offerStatus ===
        MsftCombinedFormStatus.RESALE_AUTHORIZATION_CREATION_FAILED
    )
      return 'failed'
    if (showOfferExpiredStatusBar) return 'expired'
    if (offerExpiringSoon && !isExpired) return 'expiring_soon'

    return 'in_progress'
  }, [offerStatus, isExpired, offerExpiringSoon])

  switch (combinedOfferStatus) {
    case 'failed':
      status = (
        <StatusBarV4
          key="failed"
          backgroundColor={StatusBarColorsV4.RED}
          status={'Failed'}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          content={
            microsoftCombinedFormDetails?.errorDetails &&
            microsoftCombinedFormDetails?.errorDetails?.errors ? (
              <div
                className={classes.errorSubContent}
                data-testid={'failed-offer-content'}
              >
                <div>
                  <ViewError
                    errors={microsoftCombinedFormDetails?.errorDetails?.errors}
                  />
                </div>
              </div>
            ) : (
              ''
            )
          }
        />
      )
      break
    case 'in_progress':
      status = (
        <StatusBarV4
          key="in-progress"
          statusColor={StatusBarStatusColorsV4.GREEN}
          status="In Progress"
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          divider
          content={`The offer is being created in the Microsoft Marketplace and will be ready in approximately 5 minutes. Please refresh the page periodically to check the status.`}
        />
      )
      break
    case 'accepted':
      status = (
        <StatusBarV4
          key="accepted"
          backgroundColor={StatusBarColorsV4.GREEN}
          status={'Accepted'}
          divider
          statusColor={StatusBarStatusColorsV4.GREEN}
          content={`Offer was accepted on ${[channelOfferAcceptedDate, channelOfferAcceptedTime].filter(Boolean).join(' ')} and all steps post acceptance are completed.`}
        />
      )
      break
    case 'rejected':
      status = (
        <StatusBarV4
          key="rejected"
          backgroundColor={StatusBarColorsV4.RED}
          status={'Rejected'}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                This offer has been rejected by the ISV.
              </Typography>
              <div className={classes.offerUpdatedAt}>
                {fullDateRejectedTimeForHeader}
              </div>
            </div>
          }
        />
      )
      break
    case 'extended_to_buyer':
      status = (
        <StatusBarV4
          key="extended-to-buyer"
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          status={'Extended to buyer'}
          divider
          statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
          content="Offer has been extended to the buyer by the channel partner."
        />
      )
      break
    case 'expired':
      status = (
        <StatusBarV4
          key="expired"
          backgroundColor={StatusBarColorsV4.RED}
          statusColor={StatusBarStatusColorsV4.RED}
          status="Expired"
          divider
          content={
            <div
              className={clsx(
                classes.fullWidthStatusBar,
                classes.fullWidthStatusBarWithExpiration
              )}
            >
              <Typography>
                Offer expired on {''}
                {fullDateExpirationTimeForHeader}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'expiring_soon':
      status = (
        <StatusBarV4
          key="expiring-soon"
          backgroundColor={StatusBarColorsV4.RED}
          statusColor={StatusBarStatusColorsV4.RED}
          status=""
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    padding: '0px 12px 0px 0px',
                  }}
                />
                Offer expiring in {dayDiff} days. Expires at{' '}
                {fullDateExpirationTimeForHeader}
              </Typography>
            </div>
          }
        />
      )
      break
    default:
      status = null
      break
  }

  return <div className={classes.combinedFormHeader}>{status}</div>
}
